import { useNotepadBoardIdentity } from "@/features/notepad/useNotepadBoardIdentity";
import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import VoDemo from "@/page/VoDemo";
import { FormProvider, useForm } from "@/features/tutor/context/FormContext";
import { useModalStore, ModalType } from "@/store/useModalStore";
import ModalRoot from "@/components/modal/ModalRoot";
import api, { tutorApi } from "@/utils/axios-utils";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useCooldown } from "@/features/tutor/context/CooldownContext";
import { useAuthStore } from "@/store/useAuthStore";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import { TutorMode } from "@/constants/mode";
import AssessmentModal from "@/features/tutor/components/AssessmentModal";
import AssessmentSurvey from "@/features/tutor/components/AssessmentSurvey";
import {
  trackRestartBiteSelected,
  trackRestartLessonSelected,
  trackResumeModalShown,
  trackResumeSelected,
} from "@/utils/mixpanel-utils";
import {
  addResumeDebugEvent,
  useResumeDebugStore,
} from "@/store/useResumeDebugStore";
import { useNotepadStore } from "@/features/notepad/store/useNotepadStore";
import { useNotepadPerSlideStore } from "@/features/notepad/store/useNotepadPerSlideStore";
import { getSessionVoiceConfig, getVoiceGender } from "@/constants/audioVoice";
import { ASSESSMENT_ATTEMPT_STATUS } from "@/types/assessmentAttemptStatus";
import { useStudentInputStore } from "@/features/tutor/store/useStudentInputStore";
import { useChatTrailStore } from "@/features/tutor/store/useChatTrailStore";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useBiteStatusStore } from "@/features/tutor/store/useBiteStatusStore";
import {
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import {
  clearLessonStartupPerfAnchor,
  clearSessionStartupPerfAnchor,
  getLessonStartupPerfNow,
  recordLessonStartupPerf,
  startSessionStartupPerfAnchor,
} from "@/features/tutor/utils/lessonStartupPerf";
import {
  getLessonEntryContext,
  CachedBiteMetadataSchema,
  normalizeStudentInputs,
  RESUME_ACTION,
  RESUME_FAILURE,
  RehydratePayloadSchema,
  resolveResumeFailure,
  ResumePreviewSchema,
  type CachedBiteCompletionState,
  type CachedBiteMetadata,
  type LessonEntryContext,
  type RehydratePayload,
  type ResumeAction,
  type ResumePreview,
} from "@/features/tutor/vo-demo/contracts/resumeContracts";
import { resolveSessionDiscoveryRoute } from "@/features/tutor/vo-demo/contracts/discoveryRoute";
import { useLessonStartFailure } from "@/features/tutor/vo-demo/runtime/useLessonStartFailure";
import LessonStartFailureScreen from "@/features/tutor/components/connection/LessonStartFailureScreen";
import { resolveRetakeAssessments } from "@/features/tutor/vo-demo/contracts/retakeAssessments";
import {
  clearSessionNotepadPersistence,
  clearSlideNotepadPersistence,
} from "@/features/tutor/vo-demo/utils/notepadPersistence";
import {
  hasResumeDebugParam,
  resolveResumeDebugEnabled,
} from "@/features/tutor/vo-demo/utils/resumeDebug";
import { startTutorTurn } from "@/features/tutor/utils/tutorTurnLifecycle";

interface VoDemoContainerProps {
  mode?: TutorMode;
}

type CachedBitePromptState =
  | { status: "inactive" }
  | {
      status: "announcing";
      slideIndex: number;
      completionState: Exclude<CachedBiteCompletionState, "fresh">;
      stateVersion: number;
      attentionNonce: number;
      promptKey: string;
      promptAudioSource: "session_boot" | "slide_change";
    }
  | {
      status: "prompting";
      slideIndex: number;
      completionState: Exclude<CachedBiteCompletionState, "fresh">;
      stateVersion: number;
      attentionNonce: number;
      promptKey: string;
      promptAudioSource: "session_boot" | "slide_change";
    }
  | {
      status: "applying";
      choice: "restart" | "continue";
      slideIndex: number;
      completionState: Exclude<CachedBiteCompletionState, "fresh">;
      stateVersion: number;
      attentionNonce: number;
      promptKey: string;
      promptAudioSource: "session_boot" | "slide_change";
    };

const RESUME_ACTION_FAILURE_VERB: Record<ResumeAction, string> = {
  [RESUME_ACTION.RESUME_BITE]: "resuming your lesson",
  [RESUME_ACTION.RESTART_BITE]: "restarting this bite",
  [RESUME_ACTION.RESTART_LESSON]: "restarting your lesson",
};

/**
 * A payload waiting to be put back on screen, and where its turn already is.
 *
 * The two travel together because the restore reads both, and between them
 * they answer one question: whether the tutor's audio has to be held while
 * the bite is rebuilt. A resume action's payload arrives before its turn is
 * asked for, so the hold is what stops the tutor narrating an unrestored
 * slide; a cached-bite continue's arrives with its turn already under way.
 *
 * Not optional on purpose. A producer allowed to leave it out would get a
 * default, and the default is wrong for one of the two.
 */
interface PendingRehydrate {
  payload: RehydratePayload;
  isTurnAlreadyStreaming: boolean;
}

const VoDemoContainerInner = ({ mode }: VoDemoContainerProps) => {
  const { formData } = useForm();
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const isPreview = mode === TutorMode.PREVIEW;
  const [shouldInitializeSession, setShouldInitializeSession] = useState(false);
  const [startupAuxMetadataReady, setStartupAuxMetadataReady] = useState(false);
  // Pairs with startupAuxMetadataReady: that one says an aux block has been
  // applied, this one names the action that ran between discovery and the
  // boot. Not a staleness flag -- a restart refreshes the stores from its own
  // response and still reports itself; the server decides what that permits.
  const [startChoice, setStartChoice] = useState<string | null>(null);
  const [preAssessmentModal, setPreAssessmentModal] = useState(false);
  const [preAssessmentSurvey, setPreAssessmentSurvey] = useState(false);
  const [preAssessmentRestarting, setPreAssessmentRestarting] = useState(false);
  const [sessionStartNonce, setSessionStartNonce] = useState(0);
  const [resumeTransitionMessage, setResumeTransitionMessage] = useState<
    string | null
  >(null);
  // VoDemo mounts before discovery answers, so a lesson the server is about to
  // refuse spends that round-trip looking like it is loading -- canvas, CC,
  // Exit Lesson, "Reconnecting...". VoDemo stays mounted (it prewarms the mic
  // and audio graph while we wait); this just covers it until there is
  // something real to show.
  // ponytail: no timeout on the cover. api has none either, so a hung
  // ensure_session sits here rather than on a half-drawn player. Give both a
  // deadline if that ever bites.
  const [initialDiscoveryDone, setInitialDiscoveryDone] = useState(false);
  // A denied lesson navigates away. Keep the cover up through that so the
  // player never flashes on the way out.
  const leavingAfterDenialRef = useRef(false);
  const {
    failure: lessonStartFailure,
    reportFailure: reportLessonStartFailure,
    clearFailure: clearLessonStartFailure,
    retryNow: retryLessonStart,
  } = useLessonStartFailure();
  const preAssessmentHandledRef = useRef(false);
  // Set synchronously (before any await) at the start of handleRetakeAssessment so
  // handlePreAssessmentComplete can distinguish retake completion from first-time completion.
  const isRetakingPreAssessmentRef = useRef(false);
  // Forward ref to handleRestartSession — handlePreAssessmentComplete is defined before
  // handleRestartSession, so a direct closure reference would capture a stale value.
  const restartSessionRef = useRef<() => void>(() => {});
  // Asks the next boot to restart the lesson rather than resume it. A ref, not
  // state: the boot effect reads it without needing a render to have carried
  // it, and it survives the pre-assessment gate that can sit between the
  // student's click and the boot.
  const restartRequestedRef = useRef(false);
  const resumeActionInFlightRef = useRef(false);
  const retakeInFlightRef = useRef(false);
  const discoveryRequestIdRef = useRef(0);
  const discoverSessionRef = useRef<() => Promise<void>>(async () => {});
  const sessionStartRequestedAtRef = useRef<number | null>(null);
  const preAssessmentGateSeenRef = useRef(false);
  const [lessonName, setLessonName] = useState<string | null>(null);
  const [pendingRehydrate, setPendingRehydrate] =
    useState<PendingRehydrate | null>(null);
  const [deferredRehydratePayload, setDeferredRehydratePayload] =
    useState<RehydratePayload | null>(null);
  const deferredRehydratePayloadRef = useRef<RehydratePayload | null>(null);
  useEffect(() => {
    deferredRehydratePayloadRef.current = deferredRehydratePayload;
  }, [deferredRehydratePayload]);
  const [cachedBitePromptState, setCachedBitePromptState] =
    useState<CachedBitePromptState>({ status: "inactive" });
  const cachedBitePromptStateRef = useRef<CachedBitePromptState>({
    status: "inactive",
  });
  const cachedBitePromptAudioRequestRef = useRef<string | null>(null);
  useEffect(() => {
    cachedBitePromptStateRef.current = cachedBitePromptState;
    if (cachedBitePromptState.status === "inactive") {
      cachedBitePromptAudioRequestRef.current = null;
    }
  }, [cachedBitePromptState]);
  const handleRehydrateApplied = useCallback(() => {
    addResumeDebugEvent("container:rehydrate_applied:clear_payload");
    setPendingRehydrate(null);
  }, []);

  useEffect(() => {
    console.log("[VoDemoContainer] MOUNTED");
    return () => console.log("[VoDemoContainer] UNMOUNTED");
  }, []);

  const { lessonId } = useParams();
  const { boardSessionId, acceptIdentity: acceptNotepadBoardIdentity } =
    useNotepadBoardIdentity(lessonId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const classroomId = Number(searchParams.get("classroomId")) || undefined;
  // Where a student goes when the server refuses the lesson. The classroom they
  // came from if the URL named one, otherwise their classroom list.
  const accessDeniedRoute = classroomId
    ? `/classroom/${classroomId}`
    : "/classrooms";
  const ltiLaunchRequestId = searchParams.get("ltiLaunchRequestId");
  // No ?tutor_version= override here: discovery never resolves a tutor
  // version. The boot still honours one, from a ref VoDemo builds itself.
  const discoveryUrl = "/session/lesson_discovery";
  const lessonEntryContextRef = useRef<LessonEntryContext>(
    getLessonEntryContext(searchParams),
  );
  const trackedLessonIdRef = useRef(lessonId);
  const debugEnabled = useMemo(
    () =>
      resolveResumeDebugEnabled({
        searchParams,
        isDev: import.meta.env.DEV,
        showPanelEnv: import.meta.env.VITE_SHOW_RESUME_DEBUG_PANEL,
        localStorageValue:
          typeof window !== "undefined"
            ? window.localStorage.getItem("rrdebug")
            : null,
      }),
    [searchParams],
  );
  const setResumeDebugEnabled = useResumeDebugStore(
    (state) => state.setEnabled,
  );
  const { openModal, closeModal } = useModalStore();
  const showNotification = useNotificationStore(
    (state) => state.showNotification,
  );
  const setCurrentSlideIndex = useSlideStore(
    (state) => state.setCurrentSlideIndex,
  );
  const { setCooldownSlideIndex, initializeCompletionData } = useCooldown();
  const initializeCompletionDataRef = useRef(initializeCompletionData);
  initializeCompletionDataRef.current = initializeCompletionData;
  const { setUserConfig } = useAuthStore();
  const setMeta = useAssessmentStore((state) => state.setMeta);

  const startSession = useCallback(() => {
    sessionStartRequestedAtRef.current = getLessonStartupPerfNow();
    startSessionStartupPerfAnchor({
      lesson_id: lessonId,
      mode,
      entry_source: lessonEntryContextRef.current.entrySource,
      entry_intent: lessonEntryContextRef.current.entryIntent,
      had_pre_assessment_gate: preAssessmentGateSeenRef.current,
    });
    recordLessonStartupPerf("start_session_requested", {
      lesson_id: lessonId,
      mode,
      entry_source: lessonEntryContextRef.current.entrySource,
      entry_intent: lessonEntryContextRef.current.entryIntent,
      had_pre_assessment_gate: preAssessmentGateSeenRef.current,
    });
    setShouldInitializeSession(true);
  }, [lessonId, mode]);

  useEffect(() => {
    setResumeDebugEnabled(debugEnabled);
    if (debugEnabled && hasResumeDebugParam(searchParams)) {
      window.localStorage.setItem("rrdebug", "1");
    }
    if (debugEnabled) {
      addResumeDebugEvent("debug:enabled:container", {
        lesson_id: lessonId,
      });
    }
  }, [debugEnabled, lessonId, searchParams, setResumeDebugEnabled]);

  const handleStartSession = useCallback(() => {
    addResumeDebugEvent("container:start_session");
    closeModal();
    if (!isPreview && !preAssessmentHandledRef.current) {
      const { assessments } = useAssessmentStore.getState();
      if (
        assessments.pre_assessment.has &&
        assessments.pre_assessment.state !== ASSESSMENT_ATTEMPT_STATUS.COMPLETED
      ) {
        addResumeDebugEvent("container:pre_assessment_gate");
        preAssessmentGateSeenRef.current = true;
        setPreAssessmentModal(true);
        return;
      }
    }
    startSession();
  }, [closeModal, isPreview, startSession]);

  useEffect(() => {
    if (trackedLessonIdRef.current === lessonId) {
      return;
    }
    trackedLessonIdRef.current = lessonId;
    // Keep URL entry context stable within a lesson so in-lesson query updates do not re-run discovery routing.
    lessonEntryContextRef.current = getLessonEntryContext(searchParams);
  }, [lessonId, searchParams]);

  useEffect(() => {
    discoveryRequestIdRef.current += 1;
    closeModal();
    setPendingRehydrate(null);
    setDeferredRehydratePayload(null);
    setCachedBitePromptState({ status: "inactive" });
    setPreAssessmentModal(false);
    setPreAssessmentSurvey(false);
    preAssessmentHandledRef.current = false;
    preAssessmentGateSeenRef.current = false;
    useAssessmentStore.getState().resetAll();
    return () => {
      discoveryRequestIdRef.current += 1;
      closeModal();
      setCachedBitePromptState({ status: "inactive" });
    };
  }, [closeModal, lessonId]);

  useEffect(() => {
    useUIStore.getState().resetLessonEndState();
    return () => useUIStore.getState().commitLessonExit();
  }, [lessonId, sessionStartNonce]);

  const buildResumeAnalyticsProps = useCallback(
    (data: {
      session_id?: string;
      slide_index?: number;
      state_version?: number;
    }) => ({
      session_id: data.session_id,
      lesson_id: lessonId,
      slide_index: data.slide_index,
      entry_source: lessonEntryContextRef.current.entrySource,
      state_version: data.state_version,
    }),
    [lessonId],
  );

  const parseResumePreview = useCallback(
    (value: unknown): ResumePreview | null => {
      const parsed = ResumePreviewSchema.safeParse(value);
      if (!parsed.success) {
        addResumeDebugEvent("container:resume_preview:invalid", {
          issues: parsed.error.issues,
        });
        return null;
      }
      return parsed.data;
    },
    [],
  );

  const parseRehydratePayload = useCallback(
    (value: unknown): RehydratePayload | null => {
      try {
        const parsed = RehydratePayloadSchema.safeParse(value);
        if (!parsed.success) {
          addResumeDebugEvent("container:rehydrate_payload:invalid", {
            issues: parsed.error.issues,
          });
          return null;
        }

        return {
          ...parsed.data,
          student_inputs: normalizeStudentInputs(parsed.data.student_inputs),
        };
      } catch (err: any) {
        addResumeDebugEvent("container:rehydrate_payload:parse_exception", {
          message: err?.message,
        });
        return null;
      }
    },
    [],
  );

  const parseCachedBiteMetadata = useCallback(
    (value: unknown): CachedBiteMetadata | null => {
      const parsed = CachedBiteMetadataSchema.safeParse(value);
      if (!parsed.success) {
        addResumeDebugEvent("container:cached_bite:invalid", {
          issues: parsed.error.issues,
        });
        return null;
      }

      const rehydratePayload = parsed.data.rehydrate_payload
        ? parseRehydratePayload(parsed.data.rehydrate_payload)
        : null;

      if (parsed.data.rehydrate_payload && !rehydratePayload) {
        addResumeDebugEvent("container:cached_bite:rehydrate_invalid");
      }

      return {
        ...parsed.data,
        rehydrate_payload: rehydratePayload,
      };
    },
    [parseRehydratePayload],
  );

  const showCachedBitePrompt = useCallback(
    (
      cachedBite: CachedBiteMetadata,
      fallbackSlideIndex: number,
      promptAudioSource: "session_boot" | "slide_change",
    ) => {
      if (!cachedBite.has_cached_state) {
        return false;
      }
      const slideIndex = Number(
        cachedBite.rehydrate_payload?.slide_index ?? fallbackSlideIndex,
      );
      const completionState =
        cachedBite.completion_state === "completed"
          ? "completed"
          : "in_progress";
      const stateVersion = Number(cachedBite.state_version || 0);
      const promptKey = [slideIndex, completionState, stateVersion].join(":");
      const currentPromptState = cachedBitePromptStateRef.current;
      if (
        currentPromptState.status !== "inactive" &&
        currentPromptState.promptKey === promptKey
      ) {
        return true;
      }
      setDeferredRehydratePayload(cachedBite.rehydrate_payload ?? null);
      setPendingRehydrate(null);
      setCachedBitePromptState({
        status: "announcing",
        slideIndex,
        completionState,
        stateVersion,
        attentionNonce: 0,
        promptKey,
        promptAudioSource,
      });
      addResumeDebugEvent("container:cached_bite:prompt", {
        slide_index: slideIndex,
        completion_state: completionState,
        cache_reason: cachedBite.cache_reason,
        state_version: cachedBite.state_version,
        prompt_audio_source: promptAudioSource,
      });
      return true;
    },
    [],
  );

  const triggerCachedBiteAttention = useCallback(() => {
    setCachedBitePromptState((state) => {
      if (state.status === "inactive") return state;
      return { ...state, attentionNonce: state.attentionNonce + 1 };
    });
  }, []);

  const handleCachedBiteEntry = useCallback(
    (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const cachedBite = parseCachedBiteMetadata(detail.cachedBite);
      if (!cachedBite?.has_cached_state) {
        setCachedBitePromptState({ status: "inactive" });
        setPendingRehydrate(null);
        setDeferredRehydratePayload(null);
        return;
      }
      showCachedBitePrompt(
        cachedBite,
        Number(detail.slideIndex ?? 0),
        "slide_change",
      );
    },
    [parseCachedBiteMetadata, showCachedBitePrompt],
  );

  useEffect(() => {
    window.addEventListener("vodemo:cached-bite-entry", handleCachedBiteEntry);
    return () => {
      window.removeEventListener(
        "vodemo:cached-bite-entry",
        handleCachedBiteEntry,
      );
    };
  }, [handleCachedBiteEntry]);

  // The backend refuses a slide change it cannot record, and the deck rolls
  // back to the slide the student was on. Without this the rollback is the
  // only feedback they get, so they just tap again.
  const handleSlideChangeError = useCallback(() => {
    showNotification(
      "We couldn't save your progress on that slide, so you're back on the previous one. Please try again.",
      NotificationType.WARNING,
    );
  }, [showNotification]);

  useEffect(() => {
    window.addEventListener(
      "vodemo:slide-change-error",
      handleSlideChangeError,
    );
    return () => {
      window.removeEventListener(
        "vodemo:slide-change-error",
        handleSlideChangeError,
      );
    };
  }, [handleSlideChangeError]);

  const revealCachedBiteChoices = useCallback((reason: string) => {
    setCachedBitePromptState((state) => {
      if (state.status !== "announcing") return state;
      addResumeDebugEvent("container:cached_bite:choices_revealed", {
        reason,
        slide_index: state.slideIndex,
        prompt_key: state.promptKey,
      });
      return {
        ...state,
        status: "prompting",
      };
    });
  }, []);

  useEffect(() => {
    const handleAudioStart = () => {
      revealCachedBiteChoices("audio_start");
    };

    window.addEventListener("vodemo:audio-start", handleAudioStart);
    return () => {
      window.removeEventListener("vodemo:audio-start", handleAudioStart);
    };
  }, [revealCachedBiteChoices]);

  useEffect(() => {
    if (
      cachedBitePromptState.status !== "announcing" ||
      cachedBitePromptState.promptAudioSource !== "slide_change"
    ) {
      return undefined;
    }

    const promptKey = cachedBitePromptState.promptKey;
    if (cachedBitePromptAudioRequestRef.current === promptKey) {
      return undefined;
    }
    cachedBitePromptAudioRequestRef.current = promptKey;

    let cancelled = false;
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) {
        revealCachedBiteChoices("fallback_timer");
      }
    }, 4000);

    const speakPrompt = async () => {
      try {
        await startTutorTurn((tutor_audio_on) =>
          tutorApi.post("/session/speak_cached_bite_prompt", {
            lesson_id: Number(lessonId),
            slide_index: cachedBitePromptState.slideIndex,
            expected_state_version: cachedBitePromptState.stateVersion,
            tutor_audio_on,
          }),
        );
      } catch (err: any) {
        addResumeDebugEvent("container:cached_bite_prompt_audio:error", {
          slide_index: cachedBitePromptState.slideIndex,
          status: err?.response?.status,
          message: err?.message,
        });
        if (!cancelled) {
          window.clearTimeout(fallbackTimer);
          setCachedBitePromptState((state) => {
            if (
              state.status !== "announcing" ||
              state.promptKey !== promptKey
            ) {
              return state;
            }
            return {
              ...state,
              status: "prompting",
              attentionNonce: state.attentionNonce + 1,
            };
          });
        }
      }
    };

    void speakPrompt();

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
    };
  }, [cachedBitePromptState, lessonId, revealCachedBiteChoices]);

  const applySessionDiscoveryData = useCallback(
    async (data: any) => {
      setCurrentSlideIndex(data.slide_index);
      setCooldownSlideIndex(data.slide_index);
      if (data.lesson_name) setLessonName(data.lesson_name);
      if (data.client_flags) setUserConfig(data.client_flags);
      initializeCompletionDataRef.current(data.completed_bite_indices ?? []);
      useBiteStatusStore
        .getState()
        .initFromEnsureSession(data.bite_metrics ?? {});
      useNotepadPerSlideStore
        .getState()
        .initFromEnsureSession(data.notepad_used_per_slide ?? {});

      setMeta("exit_ticket", {
        has: data.has_exit_ticket || false,
        id: data.exit_ticket_id || null,
        state: data.exit_ticket_state || null,
        attemptsCount: data.exit_ticket_attempts_count || 0,
      });

      setMeta("pre_assessment", {
        has: data.has_pre_assessment || false,
        id: data.pre_assessment_id || null,
        state: data.pre_assessment_state || null,
        attemptsCount: data.pre_assessment_attempts_count || 0,
      });

      setMeta("custom", {
        has: data.has_custom_assessment || false,
        id: data.custom_assessment_id || null,
        state: data.custom_assessment_state || null,
        attemptsCount: data.custom_assessment_attempts_count || 0,
      });
      setStartupAuxMetadataReady(true);

      window.setTimeout(() => {
        void useAssessmentStore.getState().preloadAll(Number(lessonId));
      }, 0);
    },
    [
      lessonId,
      setCooldownSlideIndex,
      setCurrentSlideIndex,
      setMeta,
      setUserConfig,
    ],
  );

  // The survey is local state here, but VoDemo's runtime and the audio/mic/chat
  // gates read the store flag. Layout so it lands before VoDemo's effects run.
  // A retake holds it until the restart has left the old slide, or the runtime
  // opens that slide's assessment (e.g. the exit ticket) mid-restart.
  useLayoutEffect(() => {
    useAssessmentStore
      .getState()
      .setIsActive(
        "pre_assessment",
        preAssessmentSurvey || preAssessmentRestarting,
      );
  }, [preAssessmentSurvey, preAssessmentRestarting]);

  const handlePreAssessmentStart = useCallback(async () => {
    setPreAssessmentModal(false);
    try {
      const store = useAssessmentStore.getState();
      await store.fetchAssessment("pre_assessment", Number(lessonId));
      const attempts = await store.fetchAttempts("pre_assessment");
      const inProgress = [...attempts]
        .reverse()
        .find(
          (a: { status: string }) =>
            a.status === ASSESSMENT_ATTEMPT_STATUS.IN_PROGRESS,
        );
      if (inProgress) {
        store.setSavedResponses("pre_assessment", inProgress.response_json);
        useAssessmentStore.setState((s: any) => ({
          assessments: {
            ...s.assessments,
            pre_assessment: {
              ...s.assessments.pre_assessment,
              attemptId: inProgress.id,
              attemptNumber: inProgress.attempt_number,
            },
          },
        }));
      } else {
        await store.startAttempt("pre_assessment");
      }
      setPreAssessmentSurvey(true);
    } catch (err) {
      console.error("Failed to start pre-assessment:", err);
      startSession();
    }
  }, [startSession]);

  const handlePreAssessmentSkip = useCallback(() => {
    preAssessmentHandledRef.current = true;
    setPreAssessmentModal(false);
    startSession();
  }, [startSession]);

  const handlePreAssessmentComplete = useCallback(() => {
    preAssessmentHandledRef.current = true;
    setPreAssessmentSurvey(false);
    if (isRetakingPreAssessmentRef.current) {
      // Retake: restart the lesson from slide 1 so the session reflects a fresh
      // start. startSession() would continue from the last known slide_index,
      // leaving the student stranded past the pre_assessment slide.
      isRetakingPreAssessmentRef.current = false;
      // Cleared by handleSessionBootComplete: the restart only asks the next
      // boot for it, so the boot's end, success or failure, is when it settles.
      setPreAssessmentRestarting(true);
      restartSessionRef.current();
    } else {
      startSession();
    }
  }, [startSession]);

  // The seven writes that make a lesson restart look like a fresh one. Shared
  // because "start the lesson over" arrives by two routes -- the learn-again
  // screen and the restart_lesson resume action -- and a clear that exists in
  // one copy cannot drift out of the other.
  const clearLessonRestartState = useCallback((sessionId: string) => {
    clearSessionNotepadPersistence(sessionId);
    useChatTrailStore.getState().clearTrail();
    useNotepadStore.getState().clearPersistedState?.();
    useNotepadStore.getState().resetPages();
    useNotepadStore.getState().close({ flush: false });
    useNotepadPerSlideStore.getState().clear();
    useStudentInputStore.getState().clearInputs();
    useSlideStore.getState().resetAllSlideProgress?.();
  }, []);

  /**
   * What the boot does once its restart has come back, before it writes any of
   * the session state. Everything here used to live in handleRestartSession,
   * next to an ensure_session of its own; the boot now makes that request, so
   * this is the half that could not move into the runtime hook -- notepad,
   * chat trail and student input are the container's to know about.
   */
  const handleRestartBootCleanup = useCallback(
    ({
      sessionId,
      slideIndex,
      clientFlags,
    }: {
      sessionId: string;
      slideIndex: number;
      clientFlags?: Record<string, unknown>;
    }) => {
      addResumeDebugEvent("container:restart_session:success", {
        slide_index: slideIndex,
      });
      setCooldownSlideIndex(slideIndex);
      if (clientFlags) setUserConfig(clientFlags);
      clearLessonRestartState(sessionId);
      // Set here rather than after the aux block the boot is about to apply:
      // this is a state write, so nothing reads it until the render that
      // follows the whole synchronous block, by which point the block is in.
      setStartupAuxMetadataReady(true);
    },
    [clearLessonRestartState, setCooldownSlideIndex, setUserConfig],
  );

  /**
   * Asks the next boot to restart rather than resume. It makes no request of
   * its own: the boot's ensure_session carries `restart: true`, which is the
   * only way the student's voice and language reach the same write that
   * replaces the session item.
   *
   * Every setter here lands in one batch with handleStartSession's, so the
   * render that starts the boot is the render that carries them -- the same
   * ordering handleApplyResumeAction relies on for startChoice.
   */
  const handleRestartSession = useCallback(() => {
    addResumeDebugEvent("container:restart_session:begin", {
      lesson_id: lessonId,
    });
    restartRequestedRef.current = true;
    setStartupAuxMetadataReady(false);
    // The same choice the modal's "Restart lesson" reports; the difference is
    // that nothing has erased the lesson yet, which restartRequestedRef says.
    setStartChoice(RESUME_ACTION.RESTART_LESSON);
    setPendingRehydrate(null);
    // Bumped even though today's callers all reach this pre-boot: the restart
    // now depends entirely on the boot effect, which does not re-run while
    // shouldInitializeSession is already true.
    setSessionStartNonce((nonce) => nonce + 1);
    handleStartSession();
  }, [handleStartSession, lessonId]);

  // Keep restartSessionRef in sync so handlePreAssessmentComplete always calls
  // the latest version of handleRestartSession despite being defined before it.
  useEffect(() => {
    restartSessionRef.current = handleRestartSession;
  }, [handleRestartSession]);

  // A resume whose checkpoint this client cannot apply. Starts the bite the
  // server already points at without restoring it -- never a restart, which
  // would erase the lesson over a payload the student had no part in.
  const startWithoutRehydrate = useCallback(() => {
    addResumeDebugEvent("container:start_without_rehydrate");
    showNotification(
      "We couldn't fully restore where you left off in this bite.",
      NotificationType.WARNING,
    );
    setPendingRehydrate(null);
    setStartChoice(null);
    handleStartSession();
  }, [handleStartSession, showNotification]);

  // Every failure of apply_resume_action lands here, and none of them restarts
  // anything. The modal is still open under the overlay, so a transient error
  // only needs saying; anything that invalidates the choice asks for it again
  // through discovery.
  const handleResumeActionFailure = useCallback(
    (action: ResumeAction, err: any) => {
      const status = err?.response?.status;
      const failure = resolveResumeFailure(status);
      addResumeDebugEvent("container:apply_resume_action:error", {
        action,
        failure,
        status,
        message: err?.message,
      });
      setResumeTransitionMessage(null);
      if (failure === RESUME_FAILURE.TRANSIENT) {
        console.error("apply_resume_action failed:", err);
        showNotification(
          `Something went wrong ${RESUME_ACTION_FAILURE_VERB[action]}. Please try again.`,
          NotificationType.WARNING,
        );
        return;
      }
      // A 404 means "nothing to resume" only to a resume; a restart that gets
      // one has simply lost the state it was choosing against.
      const isResumeGone =
        failure === RESUME_FAILURE.NOTHING_TO_RESUME &&
        action === RESUME_ACTION.RESUME_BITE;
      showNotification(
        isResumeGone
          ? "Your saved spot in this bite is no longer available. Please choose again."
          : "Your lesson changed since you opened it. Please choose again.",
        NotificationType.WARNING,
      );
      void discoverSessionRef.current();
    },
    [showNotification],
  );

  const handleApplyResumeAction = useCallback(
    async (action: ResumeAction, expectedStateVersion: number) => {
      if (resumeActionInFlightRef.current) {
        return;
      }
      // On `restart_lesson` this endpoint erases the lesson itself, so a request
      // left from an earlier click would have the boot erase it a second time.
      restartRequestedRef.current = false;
      const isRestartAction =
        action === RESUME_ACTION.RESTART_BITE ||
        action === RESUME_ACTION.RESTART_LESSON;
      const slideState = useSlideStore.getState();
      const restartBiteSlideIndex =
        action === RESUME_ACTION.RESTART_BITE
          ? Number.isInteger(slideState.pendingSlideChangeRequest?.targetIndex)
            ? slideState.pendingSlideChangeRequest?.targetIndex
            : slideState.currentSlideIndex
          : null;
      addResumeDebugEvent("container:apply_resume_action:begin", {
        action,
        expected_state_version: expectedStateVersion,
        slide_index: restartBiteSlideIndex,
      });
      resumeActionInFlightRef.current = true;
      // The modal stays up under the overlay until the server has answered:
      // closed here, a failed request left nothing on screen to try again
      // from. handleStartSession closes it on success.
      if (isRestartAction) {
        setResumeTransitionMessage(
          action === RESUME_ACTION.RESTART_BITE
            ? "Restarting bite..."
            : "Restarting lesson...",
        );
      }
      try {
        const fd = formDataRef.current;
        const voiceConfig = fd.voice?.voiceId
          ? getSessionVoiceConfig(fd.voice.voiceId)
          : null;
        const response = await tutorApi.post("/session/apply_resume_action", {
          lesson_id: Number(lessonId),
          action,
          expected_state_version: expectedStateVersion,
          ...(restartBiteSlideIndex !== null && {
            slide_index: restartBiteSlideIndex,
          }),
          language: fd.language.value,
          voice_id: voiceConfig?.voiceId ?? undefined,
          voice_model: voiceConfig?.ttsProvider ?? undefined,
          voice_gender: fd.voice?.voiceId
            ? getVoiceGender(fd.voice.voiceId)
            : undefined,
          voice_provider: voiceConfig?.ttsProvider ?? "cartesia",
          voice_speed: fd.voice?.speed,
          // The launch classroom picks which round's progress the server
          // resumes or restarts, as discovery already does.
          ...(classroomId && { classroom_id: classroomId }),
        });
        const {
          session_id,
          slide_index,
          state_version,
          rehydrate_payload,
          client_flags,
          aux_metadata_refreshed,
        } = response.data;
        addResumeDebugEvent("container:apply_resume_action:success", {
          action,
          session_id,
          slide_index,
          state_version,
          has_rehydrate_payload: !!rehydrate_payload,
        });

        setCurrentSlideIndex(slide_index);
        setCooldownSlideIndex(slide_index);
        if (client_flags) setUserConfig(client_flags);
        if (action === RESUME_ACTION.RESUME_BITE) {
          if (!rehydrate_payload) {
            addResumeDebugEvent(
              "container:apply_resume_action:missing_rehydrate_payload",
              { action, session_id },
            );
            startWithoutRehydrate();
            return;
          }

          const parsedRehydratePayload =
            parseRehydratePayload(rehydrate_payload);
          if (!parsedRehydratePayload) {
            addResumeDebugEvent(
              "container:apply_resume_action:invalid_rehydrate_payload",
              { action, session_id },
            );
            startWithoutRehydrate();
            return;
          }

          setPendingRehydrate({
            payload: parsedRehydratePayload,
            isTurnAlreadyStreaming: false,
          });
          trackResumeSelected(
            buildResumeAnalyticsProps({
              session_id,
              slide_index,
              state_version,
            }),
          );
        } else {
          setPendingRehydrate(null);
          if (action === RESUME_ACTION.RESTART_BITE) {
            trackRestartBiteSelected(
              buildResumeAnalyticsProps({
                session_id,
                slide_index,
                state_version,
              }),
            );
            clearSlideNotepadPersistence(session_id, slide_index);
            useNotepadStore.getState().clearPersistedState?.();
            useNotepadStore.getState().resetPages();
            useNotepadStore.getState().close({ flush: false });
            useNotepadPerSlideStore.getState().clearSlide(slide_index);
            useStudentInputStore.getState().clearInputs();
            useSlideStore.getState().resetSlideForRestart?.(slide_index);
          } else if (action === RESUME_ACTION.RESTART_LESSON) {
            trackRestartLessonSelected(
              buildResumeAnalyticsProps({
                session_id,
                slide_index,
                state_version,
              }),
            );
            clearLessonRestartState(session_id);
          }
        }

        // After the stores above were cleared, not before: the restart
        // branches wipe the notepad store, and this response already reflects
        // the checkpoint keys the server just deleted. Applied first, it would
        // be the thing they wiped.
        //
        // A server that does not send the block leaves the screen holding
        // pre-restart progress, so say the stores are stale and let the boot
        // rebuild them -- the behaviour every restart had before this became a
        // field in the response.
        if (isRestartAction) {
          if (aux_metadata_refreshed) {
            initializeCompletionDataRef.current(
              response.data.completed_bite_indices ?? [],
            );
            useBiteStatusStore
              .getState()
              .initFromEnsureSession(response.data.bite_metrics ?? {});
            useNotepadPerSlideStore
              .getState()
              .initFromEnsureSession(
                response.data.notepad_used_per_slide ?? {},
              );
          } else {
            setStartupAuxMetadataReady(false);
          }
          preAssessmentHandledRef.current = true;
          setSessionStartNonce((nonce) => nonce + 1);
        }
        if (!isRestartAction) {
          setResumeTransitionMessage(null);
        }
        // Batched with handleStartSession's setShouldInitializeSession so the
        // boot's bootRef sees it on the render that starts it — the ordering
        // pendingRehydrate above already relies on.
        setStartChoice(action);
        handleStartSession();
      } catch (err: any) {
        handleResumeActionFailure(action, err);
      } finally {
        resumeActionInFlightRef.current = false;
      }
    },
    [
      buildResumeAnalyticsProps,
      classroomId,
      clearLessonRestartState,
      handleResumeActionFailure,
      handleStartSession,
      lessonId,
      parseRehydratePayload,
      startWithoutRehydrate,
      setCooldownSlideIndex,
      setCurrentSlideIndex,
      setUserConfig,
    ],
  );

  const handleSessionBootComplete = useCallback(() => {
    if (sessionStartRequestedAtRef.current !== null) {
      recordLessonStartupPerf(
        "start_session_to_boot_complete",
        {
          lesson_id: lessonId,
          mode,
        },
        sessionStartRequestedAtRef.current,
      );
      sessionStartRequestedAtRef.current = null;
    }
    setResumeTransitionMessage(null);
    setPreAssessmentRestarting(false);
  }, [lessonId, mode]);

  const handleSessionBootFailed = useCallback(
    ({ retry }: { retry: () => boolean }) => {
      reportLessonStartFailure({ kind: "boot", retry });
    },
    [reportLessonStartFailure],
  );

  const handleSessionBootStarted = useCallback(() => {
    clearLessonStartFailure("boot");
  }, [clearLessonStartFailure]);

  const handleCachedBiteChoice = useCallback(
    async (choice: "restart" | "continue") => {
      const promptState = cachedBitePromptStateRef.current;
      if (promptState.status !== "prompting") {
        return;
      }

      const {
        slideIndex,
        stateVersion,
        completionState,
        attentionNonce,
        promptKey,
        promptAudioSource,
      } = promptState;
      setCachedBitePromptState({
        status: "applying",
        choice,
        slideIndex,
        completionState,
        stateVersion,
        attentionNonce,
        promptKey,
        promptAudioSource,
      });

      try {
        const response = await startTutorTurn((tutor_audio_on) =>
          tutorApi.post("/session/apply_cached_bite_choice", {
            lesson_id: Number(lessonId),
            slide_index: slideIndex,
            choice,
            expected_state_version: stateVersion,
            tutor_audio_on,
          }),
        );
        const {
          session_id,
          slide_index,
          state_version,
          turn_id,
          client_flags,
        } = response.data;

        if (client_flags) setUserConfig(client_flags);
        setCurrentSlideIndex(slide_index);
        setCooldownSlideIndex(slide_index);
        if (turn_id) {
          useTurnStore.getState().setTurnId(turn_id);
        }

        if (choice === "restart") {
          trackRestartBiteSelected(
            buildResumeAnalyticsProps({
              session_id,
              slide_index,
              state_version,
            }),
          );
          clearSlideNotepadPersistence(session_id, slide_index);
          useNotepadStore.getState().clearPersistedState?.();
          useNotepadStore.getState().resetPages();
          useNotepadStore.getState().close({ flush: false });
          useNotepadPerSlideStore.getState().clearSlide(slide_index);
          useStudentInputStore.getState().clearInputs();
          useSlideStore.getState().resetSlideForRestart?.(slide_index);
          setPendingRehydrate(null);
          setDeferredRehydratePayload(null);
          setCachedBitePromptState({ status: "inactive" });
          preAssessmentHandledRef.current = true;
          setSessionStartNonce((nonce) => nonce + 1);
          // Same erasure as the modal's restart_bite. The prompt state is
          // cleared four lines up, so the cached_bite gate no longer covers
          // this boot.
          setStartChoice(RESUME_ACTION.RESTART_BITE);
          handleStartSession();
          return;
        }

        trackResumeSelected(
          buildResumeAnalyticsProps({
            session_id,
            slide_index,
            state_version,
          }),
        );
        const deferredPayload = deferredRehydratePayloadRef.current;
        if (deferredPayload) {
          // Backend already drives this turn via cached_bite_continue; this
          // only has to put the DOM back. Arming the playback hold here would
          // clear a turn that is already under way -- and on a completed bite
          // it truncates rather than delays, because that branch awaits the
          // whole fixed line into the output queue before it answers.
          setPendingRehydrate({
            payload: deferredPayload,
            isTurnAlreadyStreaming: true,
          });
        }
        setDeferredRehydratePayload(null);
        setCachedBitePromptState({ status: "inactive" });
      } catch (err: any) {
        addResumeDebugEvent("container:cached_bite_choice:error", {
          choice,
          status: err?.response?.status,
          message: err?.message,
        });
        if (err?.response?.status === 409) {
          showNotification(
            "Session state changed. Refreshing your lesson...",
            NotificationType.WARNING,
          );
          void discoverSessionRef.current();
          return;
        }
        setCachedBitePromptState({
          status: "prompting",
          slideIndex,
          completionState,
          stateVersion,
          attentionNonce: attentionNonce + 1,
          promptKey,
          promptAudioSource,
        });
        showNotification(
          "Could not apply that choice. Please try again.",
          NotificationType.WARNING,
        );
      }
    },
    [
      buildResumeAnalyticsProps,
      handleStartSession,
      lessonId,
      setCooldownSlideIndex,
      setCurrentSlideIndex,
      setUserConfig,
      showNotification,
    ],
  );

  const handleRetakeAssessment = useCallback(
    async (type: string) => {
      if (retakeInFlightRef.current) return;
      retakeInFlightRef.current = true;
      if (type === "pre_assessment") {
        // Set flag synchronously so handlePreAssessmentComplete knows to call
        // handleRestartSession instead of startSession when the survey closes.
        isRetakingPreAssessmentRef.current = true;
        // Force-unmount the existing pre_assessment UI before async work begins.
        // Without this, the old survey is still mounted when setPreAssessmentSurvey(true)
        // is called later, making it a no-op (React sees no state change).
        setPreAssessmentSurvey(false);
        setPreAssessmentModal(false);
      }
      const store = useAssessmentStore.getState();
      // isActive: false so the survey doesn't mount until savedResponses is
      // set at the end of the try block. Set to true there after all async work.
      useAssessmentStore.setState((s: any) => ({
        assessments: {
          ...s.assessments,
          [type]: {
            ...s.assessments[type],
            isActive: false,
            attemptId: null,
            attemptNumber: null,
            completed: false,
            score: null,
            savedResponses: null,
          },
        },
      }));

      try {
        // No ensure_session here. It used to run for `slide_index` and
        // `client_flags`, both of which discovery wrote moments ago and nothing
        // can move while this modal is up -- and it was not free: the server
        // refuses the aux deferral for a discovery-shaped call, so it rebuilt
        // the whole aux block for a caller that read two fields, and it still
        // validated the LTI launch context it forwarded, failing the retake
        // outright for a Clever student whose launch expired mid-modal.
        //
        // startupAuxMetadataReady is deliberately left alone: the two calls
        // below leave the local assessment state newer than discovery's, and a
        // boot that defers keeps it rather than overwriting it with a re-read.
        await store.fetchAssessment(type, Number(lessonId));
        await store.startAttemptRestoringResponses(type);
        if (type === "pre_assessment") {
          preAssessmentHandledRef.current = true;
        } else {
          // Mount the survey only now, after savedResponses is set.
          store.setIsActive(type, true);
        }
        closeModal();
        if (type === "pre_assessment") {
          setPreAssessmentSurvey(true);
        }
      } catch (err) {
        console.error(`Failed to retake ${type}:`, err);
        if (type === "pre_assessment")
          isRetakingPreAssessmentRef.current = false;
        store.setIsActive(type, false);
        store.setSavedResponses(type, null);
        showNotification(
          "Could not restart the assessment. Please try again.",
          NotificationType.WARNING,
        );
      } finally {
        retakeInFlightRef.current = false;
      }
    },
    [closeModal, lessonId, showNotification],
  );

  const discoverSessionAndRoute = useCallback(async () => {
    const requestId = ++discoveryRequestIdRef.current;
    // A failed boot is moot once discovery re-reads everything. A failed
    // discovery stays on screen through its own retry; see the finally below.
    clearLessonStartFailure("boot");
    let hasDiscoveryFailed = false;
    closeModal();
    setPendingRehydrate(null);
    setDeferredRehydratePayload(null);
    setStartupAuxMetadataReady(false);
    // Discovery is about to re-read everything, so whatever the student chose
    // before it no longer stands between them and the boot.
    setStartChoice(null);
    setPreAssessmentModal(false);
    setPreAssessmentSurvey(false);
    preAssessmentHandledRef.current = false;
    preAssessmentGateSeenRef.current = false;
    isRetakingPreAssessmentRef.current = false;
    restartRequestedRef.current = false;
    const discoveryStartedAt = getLessonStartupPerfNow();
    try {
      // These three keep `ensure_session` in their names though discovery no
      // longer calls it: they are dashboard series, and renaming them breaks
      // continuity at the deploy.
      recordLessonStartupPerf("discovery_ensure_session_begin", {
        lesson_id: lessonId,
        mode,
      });
      const response = await tutorApi.post(discoveryUrl, {
        lesson_id: lessonId,
        ...(classroomId && { classroom_id: classroomId }),
        ...(ltiLaunchRequestId && {
          lti_launch_request_id: ltiLaunchRequestId,
        }),
      });
      const data = response.data;
      if (requestId !== discoveryRequestIdRef.current) {
        return;
      }
      addResumeDebugEvent("container:ensure_session:discovery", {
        session_id: data.session_id,
        is_existing_session: data.is_existing_session,
        slide_index: data.slide_index,
        resume_available: data.resume_available,
        resume_runtime_available: data.resume_runtime_available,
        state_version: data.state_version,
      });
      recordLessonStartupPerf(
        "discovery_ensure_session_success",
        {
          lesson_id: lessonId,
          mode,
          session_id: data.session_id,
          slide_index: data.slide_index,
          status: response.status,
        },
        discoveryStartedAt,
      );

      await applySessionDiscoveryData(data);
      if (requestId !== discoveryRequestIdRef.current) {
        return;
      }
      if (data.session_id && lessonId) {
        acceptNotepadBoardIdentity({ lessonId, sessionId: data.session_id });
      }

      const routeDecision = resolveSessionDiscoveryRoute({
        data,
        entryContext: lessonEntryContextRef.current,
        isPreview,
      });

      // Every route below opens the same screen and differs only in the
      // actions it offers. Preview has no saved progress to reason about, so
      // it gets the plain start and no retake links.
      const retakeItems = resolveRetakeAssessments({
        hasExitTicket: data.has_exit_ticket,
        exitTicketState: data.exit_ticket_state,
        hasPreAssessment: data.has_pre_assessment,
        preAssessmentState: data.pre_assessment_state,
      });

      if (routeDecision.kind === "preview_start") {
        addResumeDebugEvent("container:routing:preview_start");
        openModal(ModalType.SESSION_START, {
          actions: [
            {
              id: "start",
              label: "Start Learning",
              kind: "primary",
              onSelect: handleStartSession,
            },
          ],
        });
        return;
      }

      if (routeDecision.kind === "learn_again_modal") {
        addResumeDebugEvent("container:routing:learn_again_modal");
        openModal(ModalType.SESSION_START, {
          actions: [
            {
              id: "start",
              label: "Start Learning",
              // No pending label any more: the restart stopped making a request
              // of its own, so this closes as synchronously as the plain start
              // and a spinner would never have a frame to appear in.
              kind: "primary",
              onSelect: handleRestartSession,
            },
          ],
          retakeItems,
          onRetakeAssessment: handleRetakeAssessment,
        });
        return;
      }

      if (routeDecision.kind === "resume_restart_modal") {
        addResumeDebugEvent("container:routing:resume_restart_modal", {
          reason: routeDecision.reason,
          state_version: routeDecision.stateVersion,
        });
        const resumePreview = routeDecision.hasResumeCheckpoint
          ? parseResumePreview(data.resume_preview)
          : null;
        if (routeDecision.hasResumeCheckpoint && !resumePreview) {
          addResumeDebugEvent(
            "container:routing:resume_restart_missing_preview",
          );
        }
        trackResumeModalShown(
          buildResumeAnalyticsProps({
            session_id: data.session_id,
            slide_index: resumePreview?.slide_index ?? data.slide_index,
            state_version: routeDecision.stateVersion,
          }),
        );
        openModal(ModalType.SESSION_START, {
          actions: [
            {
              id: "restart_bite",
              label: "Restart Bite",
              pendingLabel: "Restarting...",
              kind: "secondary",
              onSelect: () =>
                handleApplyResumeAction(
                  RESUME_ACTION.RESTART_BITE,
                  routeDecision.stateVersion,
                ),
            },
            {
              id: "resume",
              label: "Resume",
              pendingLabel: "Resuming...",
              kind: "primary",
              onSelect: () => {
                if (resumePreview) {
                  return handleApplyResumeAction(
                    RESUME_ACTION.RESUME_BITE,
                    routeDecision.stateVersion,
                  );
                }
                trackResumeSelected(
                  buildResumeAnalyticsProps({
                    session_id: data.session_id,
                    slide_index: data.slide_index,
                    state_version: routeDecision.stateVersion,
                  }),
                );
                setPendingRehydrate(null);
                handleStartSession();
              },
            },
            {
              id: "restart_lesson",
              label: "Restart lesson",
              pendingLabel: "Restarting...",
              kind: "link",
              onSelect: () =>
                handleApplyResumeAction(
                  RESUME_ACTION.RESTART_LESSON,
                  routeDecision.stateVersion,
                ),
              confirm: {
                title: "Restart from beginning?",
                message:
                  "If you restart, your progress in this lesson will be erased. You won't be able to get it back.",
                confirmLabel: "Restart Lesson",
              },
            },
          ],
          retakeItems,
          sessionLanguage:
            typeof data.session_language === "string"
              ? data.session_language
              : null,
          lessonId,
          onRetakeAssessment: handleRetakeAssessment,
        });
        return;
      }

      if (routeDecision.reason === "runtime_unavailable") {
        addResumeDebugEvent("container:routing:runtime_unavailable_fallback");
      } else {
        addResumeDebugEvent("container:routing:initialization_modal");
      }
      openModal(ModalType.SESSION_START, {
        actions: [
          {
            id: "start",
            label: "Start Learning",
            kind: "primary",
            onSelect: handleStartSession,
          },
        ],
        retakeItems,
        onRetakeAssessment: handleRetakeAssessment,
      });
    } catch (error) {
      if (requestId !== discoveryRequestIdRef.current) {
        return;
      }
      if ((error as any)?.response?.status === 403) {
        clearLessonStartupPerfAnchor();
        clearSessionStartupPerfAnchor();
        // The player shell is already mounted by the time discovery runs, so
        // returning here left the student in a dead lesson -- grey canvas,
        // "Reconnecting..." forever -- behind a native alert. No session was
        // ever created, so there is nothing here to stay for. Leave.
        showNotification(
          (error as any)?.response?.data?.detail ||
            "You do not have access to this lesson.",
          NotificationType.WARNING,
        );
        leavingAfterDenialRef.current = true;
        navigate(accessDeniedRoute, { replace: true });
        return;
      }
      console.error("Failed to discover session and route:", error);
      recordLessonStartupPerf(
        "discovery_ensure_session_error",
        {
          lesson_id: lessonId,
          mode,
          status: (error as any)?.response?.status,
          message: error instanceof Error ? error.message : String(error),
        },
        discoveryStartedAt,
      );
      clearLessonStartupPerfAnchor();
      clearSessionStartupPerfAnchor();
      addResumeDebugEvent("container:routing:error", {
        message: error instanceof Error ? error.message : String(error),
      });
      // Discovery closed the start modal before asking, so failing quietly
      // leaves the student on a player with nothing behind its buttons.
      hasDiscoveryFailed = true;
      reportLessonStartFailure({
        kind: "discovery",
        retry: () => {
          void discoverSessionRef.current();
          return true;
        },
      });
    } finally {
      // Not for a superseded request -- a stale answer must not uncover the
      // player while the current discovery is still deciding.
      if (
        !leavingAfterDenialRef.current &&
        requestId === discoveryRequestIdRef.current
      ) {
        setInitialDiscoveryDone(true);
        if (!hasDiscoveryFailed) clearLessonStartFailure("discovery");
      }
    }
  }, [
    acceptNotepadBoardIdentity,
    accessDeniedRoute,
    applySessionDiscoveryData,
    buildResumeAnalyticsProps,
    clearLessonStartFailure,
    closeModal,
    discoveryUrl,
    handleApplyResumeAction,
    handleRestartSession,
    handleRetakeAssessment,
    handleStartSession,
    isPreview,
    lessonId,
    ltiLaunchRequestId,
    mode,
    navigate,
    openModal,
    parseResumePreview,
    reportLessonStartFailure,
    showNotification,
  ]);

  // Cover the next lesson too. Next lesson navigates /vo-demo/A -> /vo-demo/B
  // in place, but the student route remounts this container for a new lesson
  // (RemountOnLessonChange). Any route without that boundary keeps the route
  // component mounted: discovery reruns, and a stale `true` here would leave
  // the previous lesson's player on screen for the whole request. Keyed on
  // `lessonId` alone: the mid-session recovery discoveries do not change it
  // and must stay uncovered.
  // Layout, not passive: a passive effect runs after the browser paints, so the
  // render that carries the new lessonId would paint once with the old lesson's
  // `true` still in place -- one uncovered frame of the previous player. This
  // runs before that paint.
  useLayoutEffect(() => {
    setInitialDiscoveryDone(false);
    leavingAfterDenialRef.current = false;
  }, [lessonId]);

  useEffect(() => {
    discoverSessionRef.current = discoverSessionAndRoute;
  }, [discoverSessionAndRoute]);

  useEffect(() => {
    discoverSessionRef.current();
  }, [discoverSessionAndRoute]);

  const activeRehydrate =
    cachedBitePromptState.status !== "inactive" ? null : pendingRehydrate;

  return (
    <>
      {/* `display: contents` so the wrapper generates no box -- VoDemo's own
          `w-full h-full` root keeps sizing against `.fullscreen` exactly as
          before. The wrapper exists only to carry `inert`, which the cover
          below cannot do on its own: a stacked div hides the player visually
          but leaves every control in the accessibility tree and reachable by
          Tab. `inert` removes the subtree from both.
          React 18 has no boolean `inert` prop, hence the string spread.
          Safari < 15.5 ignores the attribute and degrades to a visual-only
          cover, which is where this started. */}
      <div
        style={{ display: "contents" }}
        {...(initialDiscoveryDone && !lessonStartFailure
          ? {}
          : ({ inert: "" } as { inert?: string }))}
      >
        <VoDemo
          lessonId={lessonId}
          boardSessionId={boardSessionId}
          onSessionEstablished={acceptNotepadBoardIdentity}
          mode={mode}
          shouldInitializeSession={shouldInitializeSession}
          sessionStartNonce={sessionStartNonce}
          pendingRehydratePayload={activeRehydrate?.payload ?? null}
          shouldHoldResumePlayback={
            activeRehydrate !== null && !activeRehydrate.isTurnAlreadyStreaming
          }
          startChoice={startChoice}
          restartRequestedRef={restartRequestedRef}
          onRestartCleanup={handleRestartBootCleanup}
          startupAuxMetadataReady={startupAuxMetadataReady}
          setStartupAuxMetadataReady={setStartupAuxMetadataReady}
          cachedBitePromptState={cachedBitePromptState}
          onCachedBiteChoice={handleCachedBiteChoice}
          onCachedBiteAttention={triggerCachedBiteAttention}
          onRehydrateApplied={handleRehydrateApplied}
          onSessionBootComplete={handleSessionBootComplete}
          onSessionBootFailed={handleSessionBootFailed}
          onSessionBootStarted={handleSessionBootStarted}
        />
      </div>
      <ModalRoot />
      {!initialDiscoveryDone && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-white"
        >
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#1C9C6D] border-t-transparent" />
          <span className="sr-only">Loading your lesson</span>
        </div>
      )}
      {initialDiscoveryDone && lessonStartFailure && (
        <LessonStartFailureScreen
          kind={lessonStartFailure.kind}
          isRetrying={lessonStartFailure.isRetrying}
          onRetry={retryLessonStart}
        />
      )}
      {resumeTransitionMessage && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="rounded-lg border border-gray-200 bg-white px-5 py-4 text-center shadow-lg">
            <div className="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-[#1C9C6D] border-t-transparent" />
            <p className="font-mulish text-base font-semibold text-[#252525]">
              {resumeTransitionMessage}
            </p>
          </div>
        </div>
      )}
      {!isPreview && preAssessmentModal && (
        <AssessmentModal
          type="pre_assessment"
          onStart={handlePreAssessmentStart}
          onSkip={handlePreAssessmentSkip}
          lessonName={lessonName}
        />
      )}
      {!isPreview && preAssessmentSurvey && (
        <AssessmentSurvey
          type="pre_assessment"
          onComplete={handlePreAssessmentComplete}
          onSaveAndExit={handlePreAssessmentComplete}
        />
      )}
    </>
  );
};

const VoDemoContainer = ({ mode = TutorMode.PUBLIC }: VoDemoContainerProps) => (
  <FormProvider mode={mode}>
    <VoDemoContainerInner mode={mode} />
  </FormProvider>
);

export default VoDemoContainer;
